import { Content } from "./components/Content";
import { Footer } from "./components/Footer";
import { Heading } from "./components/Heading";
import { Career } from "./components/Career";
import { Logo } from "./components/Logo";
import { TeamMember } from "./components/TeamMember";
import { Text } from "./components/Text";
import { TextContainer } from "./components/TextContainer";
import { Preloader } from "./components/Preloader";
import { PreloaderContext } from "./contexts/PreloaderContext";
import Lenis from "lenis";
import { useEffect, useMemo, useRef, useState } from "react";
import gsap from "gsap";
import "./index.css";

export function App() {
  const headerRef = useRef<HTMLDivElement>(null);
  const [preloaderComplete, setPreloaderComplete] = useState(false);

  const preloaderContextValue = useMemo(
    () => ({ isComplete: preloaderComplete }),
    [preloaderComplete],
  );

  useEffect(() => {
    const lenis = new Lenis({
      duration: 0.8,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // easeOutExpo
      smoothWheel: true,
      anchors: true,
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    // Initial hero animation - only after preloader completes
    if (headerRef.current && preloaderComplete) {
      const titles = headerRef.current.querySelectorAll(".header__title");
      const logo = headerRef.current.querySelector(".logo");

      gsap.fromTo(
        logo,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1, ease: "power2.out" },
      );

      gsap.fromTo(
        titles,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          stagger: 0.2,
          ease: "power2.out",
          delay: 0.3,
        },
      );
    }

    return () => {
      lenis.destroy();
    };
  }, [preloaderComplete]);

  return (
    <PreloaderContext.Provider value={preloaderContextValue}>
      <div className="app">
        <Preloader onComplete={() => setPreloaderComplete(true)} />
        <Content>
          <div className="header" ref={headerRef}>
            <Logo />
            <h1 className="header__title header__title--one">
              Cancer moves fast.
            </h1>
            <h1 className="header__title">Serova moves faster.</h1>
          </div>
          <TextContainer>
            <Text>
              We're building personalised cancer vaccines designed around tumour
              dynamics - targeting what will matter, not just what's visible
              today.
            </Text>
            <Text>
              Interested in joining our mission?{" "}
              <a className="__link" href="#careers">
                We're hiring
              </a>
              .
            </Text>
            <Text>We're based in London, United Kingdom.</Text>
          </TextContainer>
          <TextContainer>
            <Heading title="The Problem" />
            <Text>
              Cancer vaccines can work. In a subset of patients, they induce
              complete and durable remissions when other therapies fail. But for
              most patients, they don't. This isn't because the immune system
              can't recognise cancer. It's because the information used to
              design the vaccine is already out of date.
            </Text>
            <Text>
              Tumours are heterogeneous and unstable. Conventional vaccine
              design relies on a single snapshot of disease – taken invasively,
              from one site, at one moment in time. By the time a vaccine is
              designed, manufactured, and delivered, the tumour has often
              changed. The immune response is directed at targets that no longer
              reflect the disease as it exists.
            </Text>
          </TextContainer>
          <TextContainer>
            <Heading title="Our Approach" />
            <Text>
              We rebuilt vaccine design around a simple idea: base target
              selection on a view of the tumour that reflects the disease as it
              actually exists.
            </Text>
            <Text>
              Our platform identifies vaccine targets from sources that capture
              tumour biology across sites and over time, without the constraints
              of conventional sampling. This provides a more complete and
              current picture of the disease.
            </Text>
            <Text>
              From that picture, we select the mutations driving tumour growth.
              The result is vaccines designed for each patient and for the state
              of their disease when treatment is delivered.
            </Text>
          </TextContainer>
          <TextContainer>
            <Heading title="Our Team" />
            <TeamMember
              name="Matt Dean"
              role="Co-founder"
              description="Ex-MD-PhD at Oxford. Previously at Granza Bio (YC W24) and led an Oxford accelerator responsible for multiple successful spin-outs. Lost his leg as a child and now plays amputee football professionally for England."
              linkedInUri="https://www.linkedin.com/in/matt-dean-4a12a0289/"
            />
            <TeamMember
              name="Michael Bryan"
              role="Co-founder"
              description="MD-PhD candidate at Oxford. Trained in machine learning at the Broad Institute of MIT and Harvard. Has contributed to the design of cancer vaccine candidates heading towards the clinic."
              linkedInUri="https://www.linkedin.com/in/michaelbryanox/"
            />
          </TextContainer>
          <TextContainer id="careers">
            <Heading title="Careers" />
            <Text>
              We're building a team that's comfortable moving between theory and
              biology, and between uncertainty and execution. If you've felt
              that computational oncology moves too slowly, or that the most
              interesting problems get diluted before they reach production,
              this will feel different.
            </Text>
            <Text>We're looking for people who:</Text>
            <Text tag="ul">
              <li>Have built systems that worked under uncertainty</li>
              <li>
                Are comfortable with incomplete data and ambiguous
                specifications
              </li>
              <li>
                Can explain clearly what they're building and why it matters
              </li>
            </Text>
            <Text>
              What we care about most is whether you can solve hard problems
              that haven't been solved before.
            </Text>
            <div className="careers">
              <Career
                title="ML Engineer"
                location="London, United Kingdom"
                description="Strong mathematics background preferable."
              ></Career>
              <Career
                title="Computational Biologist"
                location="London, United Kingdom"
                description="For those proficient in science and programming."
              ></Career>
              <Career
                title="Bioinformatician"
                location="London, United Kingdom"
                description="Analyse and interpret complex biological data."
              ></Career>
              <Career
                title="Operations"
                location="London, United Kingdom"
                description="Help us run our day-to-day operations smoothly."
              ></Career>
            </div>
          </TextContainer>
        </Content>
        <Footer />
      </div>
    </PreloaderContext.Provider>
  );
}

export default App;
